<template>
    <Modal
        :title="t({ key: 'service_package.select_service_package' })"
        height="auto"
        size="xl"
        position="top"
        scroll="box"
        :padding="modalPadding"
        :errors="errors"
        :onClose="closeModal"
        :onCloseWarning="totalPrice?.Amount > 0">
        <Selection
            :addons="addons"
            :onSelect="handleSelection"
            :selectedAddon="selectedAddon" />
        <template #footer>
            <Footer
                :totalPrice="totalPrice"
                :disabled="!selectedAddon"
                :onClick="finalizeAddons"
                :loading="footerIsLoading"
                :priceText="t({ key: 'upsell.total_for_entire_booking' })"
                :buttonLabel="t({ key: 'upsell.to_payment' })"
                buttonColor="jungle" />
        </template>
    </Modal>
</template>

<script lang="ts">
    import { computed, defineComponent, nextTick, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { useStore } from '@/store';
    import { t } from '@shared/helpers/translation';
    import { endpoints } from '@/api/endpoints';
    import {
        ResponseStatus,
        UpSellAddonServicePackage,
        UpSellType,
    } from '@/api/MyBookingSession/myBookingSession.interfaces';
    import { MyBookingCredentials } from '@/api/MyBookingCredentials/myBookingCredentials.interfaces';
    import { Money } from '@libs/money/money.interfaces';
    import { ReducerStatus } from '@/store/types';
    import { logAnalyticsMyBookingDimensionEvents } from '@/tracking/analytics/MyBooking/Functions/my-booking-session.functions';
    import {
        logStatsigBookingServicePackageAddonCompleted,
        logStatsigBookingServicePackageInteraction,
        logStatsigServicePackageClose,
    } from '@/tracking/statsig/booking.functions';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { EventName } from '@/tracking/analytics/types';
    import Modal from '@ui/components/Modal/Modal.vue';
    import Footer from '../../../myBookingPage/Sections/UpSell/Modals/Shared/Footer/Footer.vue';
    import Selection from './Selection/Selection.vue';

    export default defineComponent({
        name: 'ServicePackageModal',
        methods: { t },
        components: {
            Footer,
            Modal,
            Selection,
        },
        setup() {
            const store = useStore();
            const router = useRouter();
            const { isTablet } = useScreenSize();
            const selectedAddon = ref<UpSellAddonServicePackage | undefined>();
            const errors = ref<string[]>([]);

            const modalPadding = computed(() => (isTablet.value ? 'm' : 's'));

            const credentials = computed<MyBookingCredentials>(
                () => store.getters['myBookingCredentials/getCredentials']
            );

            const totalPrice = computed<Money>(() => {
                return {
                    Amount: selectedAddon.value?.Price?.Amount ?? 0,
                    Currency: addons.value[0]?.Price?.Currency ?? '',
                };
            });

            const addons = computed<UpSellAddonServicePackage[]>(
                () => store.getters['myBookingSession/getServicePackageAddons'] ?? []
            );

            const footerIsLoading = computed(() =>
                addons.value.some((addon) => addon.Status === ReducerStatus.UPDATING)
            );

            watch(
                () => selectedAddon.value,
                (newValue, oldValue) => {
                    if (newValue) {
                        logStatsigBookingServicePackageInteraction('Select', newValue);
                    }
                    if (oldValue) {
                        logStatsigBookingServicePackageInteraction('UnSelect', oldValue);
                    }
                }
            );

            const finalizeAddons = () => {
                errors.value = [];

                if (credentials.value && selectedAddon.value) {
                    const { id, password } = credentials.value;
                    const addon = selectedAddon.value;

                    store.dispatch('myBookingSession/setAddonStatus', {
                        addon,
                        status: ReducerStatus.UPDATING,
                    });

                    endpoints.myBookingSession
                        .SaveBookingSession(id, password, [addon])
                        .then((r) => {
                            if (r.status === ResponseStatus.Redirect) {
                                router.push({
                                    name: 'payment',
                                    query: router.currentRoute.value.query,
                                });

                                logAnalyticsMyBookingDimensionEvents(
                                    EventName.MY_BOOKING_UPSELL_COMPLETED,
                                    UpSellType.ServicePackage
                                );

                                logStatsigBookingServicePackageAddonCompleted(addon);
                            } else if (r.status === ResponseStatus.Error) {
                                errors.value = r.errors;
                            }
                        })
                        .finally(() => store.commit('general/setModal', null));
                }
            };

            const closeModal = computed(() => {
                return !footerIsLoading.value
                    ? () => {
                          logStatsigServicePackageClose();
                          nextTick(() => store.commit('general/setModal', null));
                      }
                    : undefined;
            });

            const handleSelection = (addon: UpSellAddonServicePackage) => {
                selectedAddon.value = addon;
            };

            return {
                selectedAddon,
                footerIsLoading,
                addons,
                totalPrice,
                closeModal,
                errors,
                modalPadding,
                finalizeAddons,
                handleSelection,
            };
        },
    });
</script>
